import { makeAutoObservable, runInAction } from "mobx";
import { showToast } from "@/lib/utils/toast";
import { t } from "i18next";
import { questionLibraryService } from "../services/question-library.service";

// ─── CONSTANTS ───────────────────────────────────────────────────────────────

const DEFAULT_FILTERS = {
  search: "",
  category: "all",
  type: "all",
  sort: "recently_added",
  showFavourites: false,
  showArchived: false,
};

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 12,
  total: 0,
  pages: 1,
};

const DEFAULT_STATS = {
  total: 0,
  categories: 0,
  favourites: 0,
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const buildQueryParams = (filters, pagination) => {
  const params = {
    page: pagination.page,
    limit: pagination.limit,
    sort: filters.sort,
  };

  if (filters.search.trim()) params.search = filters.search.trim();
  if (filters.category !== "all") params.category = filters.category;
  if (filters.type !== "all") params.type = filters.type;
  if (filters.showFavourites) params.favourites = true;
  if (filters.showArchived) params.archived = true;

  return params;
};

// ─── STORE ───────────────────────────────────────────────────────────────────

class QuestionLibraryStore {
  // State
  questions = [];
  stats = { ...DEFAULT_STATS };
  filters = { ...DEFAULT_FILTERS };
  pagination = { ...DEFAULT_PAGINATION };

  // UI State
  loading = false;
  submitting = false;
  initializing = true;
  isModalOpen = false;
  editingQuestion = null; // null = create mode, object = edit mode
  _fetchId = 0;
  _initialized = false;

  constructor() {
    makeAutoObservable(this);
  }

  // ─── COMPUTED ─────────────────────────────────────────────────────────

  get isEditMode() {
    return this.editingQuestion !== null;
  }

  get hasQuestions() {
    return this.questions.length > 0;
  }

  get paginationInfo() {
    const { page, limit, total } = this.pagination;
    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);
    return { start, end, total };
  }

  get canGoPrev() {
    return this.pagination.page > 1;
  }

  get canGoNext() {
    return this.pagination.page < this.pagination.pages;
  }

  // ─── RESET ────────────────────────────────────────────────────────────

  resetFilters = () => {
    this.filters = { ...DEFAULT_FILTERS };
    this.pagination = { ...DEFAULT_PAGINATION };
  };

  resetState = () => {
    this.questions = [];
    this.stats = { ...DEFAULT_STATS };
    this.filters = { ...DEFAULT_FILTERS };
    this.pagination = { ...DEFAULT_PAGINATION };
    this.loading = false;
    this.submitting = false;
    this.initializing = true;
    this.isModalOpen = false;
    this.editingQuestion = null;
  };

  // ─── FILTER SETTERS ───────────────────────────────────────────────────

  setSearch = (value) => {
    this.filters.search = value;
    this.pagination.page = 1; // reset to first page on new search
  };

  setCategory = (value) => {
    this.filters.category = value;
    this.pagination.page = 1;
  };

  setType = (value) => {
    this.filters.type = value;
    this.pagination.page = 1;
  };

  setSort = (value) => {
    this.filters.sort = value;
    this.pagination.page = 1;
  };
  setFilter(key, value) {
    this.filters[key] = value;
    this.pagination.page = 1;
  }

  toggleShowFavourites = () => {
    this.filters.showFavourites = !this.filters.showFavourites;
    this.filters.showArchived = false; // mutually exclusive
    this.pagination.page = 1;
  };

  toggleShowArchived = () => {
    this.filters.showArchived = !this.filters.showArchived;
    this.filters.showFavourites = false; // mutually exclusive
    this.pagination.page = 1;
  };

  // ─── PAGINATION ───────────────────────────────────────────────────────

  setPage = (page) => {
    this.pagination.page = page;
  };

  goNextPage = () => {
    if (this.canGoNext) this.pagination.page += 1;
  };

  goPrevPage = () => {
    if (this.canGoPrev) this.pagination.page -= 1;
  };

  // ─── MODAL ────────────────────────────────────────────────────────────

  openCreateModal = () => {
    this.editingQuestion = null;
    this.isModalOpen = true;
  };

  openEditModal = (question) => {
    this.editingQuestion = question;
    this.isModalOpen = true;
  };

  closeModal = () => {
    this.isModalOpen = false;
    this.editingQuestion = null;
  };

  // ─── INITIALIZE ───────────────────────────────────────────────────────

  initialize = async () => {
    runInAction(() => {
      this.initializing = true;
    });
    await this.fetchQuestions();
    runInAction(() => {
      this.initializing = false;
    });
  };

  // ─── FETCH QUESTIONS ──────────────────────────────────────────────────
  fetchQuestions = async () => {
    runInAction(() => {
      this.loading = true;
    });

    try {
      const params = buildQueryParams(this.filters, this.pagination);
      console.log("PARAMS SENT:", JSON.stringify(params)); // ✅ debug
      const response = await questionLibraryService.listQuestions(params);
      const data = response?.data || response;

      // fetchQuestions in store — no fallback chains needed anymore
      runInAction(() => {
        this.questions = data?.result || [];
        this.pagination = {
          ...this.pagination,
          total: data?.total ?? 0,
          pages: data?.pages ?? 1,
        };
        if (data?.stats) {
          this.stats = {
            total: data.stats.total ?? 0,
            categories: data.stats.categories ?? 0,
            favourites: data.stats.favourites ?? 0,
          };
        }
      });
    } catch (err) {
      showToast.error(err?.message || t("Unable to load questions."));
      runInAction(() => {
        this.questions = [];
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  // ─── CREATE QUESTION ──────────────────────────────────────────────────

  createQuestion = async (formData) => {
    runInAction(() => {
      this.submitting = true;
    });

    try {
      const response = await questionLibraryService.createQuestion(formData);
      const data = response?.data || response;
      const newQuestion = data?.question || data;

      runInAction(() => {
        // Optimistic: prepend to list if on first page
        if (this.pagination.page === 1) {
          this.questions = [newQuestion, ...this.questions].slice(
            0,
            this.pagination.limit,
          );
        }
        this.stats.total += 1;
        this.isModalOpen = false;
        this.editingQuestion = null;
      });

      showToast.success(t("Question created successfully."));

      // Refresh to get accurate data
      await this.fetchQuestions();

      return newQuestion;
    } catch (err) {
      showToast.error(err?.message || t("Failed to create question."));
      throw err;
    } finally {
      runInAction(() => {
        this.submitting = false;
      });
    }
  };

  // ─── UPDATE QUESTION ──────────────────────────────────────────────────
  updateQuestion = async (id, formData) => {
    runInAction(() => {
      this.submitting = true;
    });

    try {
      const response = await questionLibraryService.updateQuestion(
        id,
        formData,
      );
      const data = response?.data || response;

      // ✅ API returns { question: {...} }
      const updatedQuestion = data?.question || data;

      runInAction(() => {
        this.questions = this.questions.map((q) =>
          q._id === id ? updatedQuestion : q,
        );
        this.isModalOpen = false;
        this.editingQuestion = null;
      });

      showToast.success(t("Question updated successfully."));
      await this.fetchQuestions();
      return updatedQuestion;
    } catch (err) {
      showToast.error(err?.message || t("Failed to update question."));
      throw err;
    } finally {
      runInAction(() => {
        this.submitting = false;
      });
    }
  };

  // ─── SUBMIT (CREATE OR UPDATE) ────────────────────────────────────────

  submitQuestion = async (formData) => {
    if (this.isEditMode) {
      return this.updateQuestion(this.editingQuestion._id, formData);
    }
    return this.createQuestion(formData);
  };

  // ─── TOGGLE FAVOURITE ─────────────────────────────────────────────────

  toggleFavourite = async (id) => {
    // Optimistic update
    const prevQuestions = [...this.questions];
    const target = this.questions.find((q) => q._id === id);
    if (!target) return;

    const willBeFavourite = !target.isFavourite;

    runInAction(() => {
      this.questions = this.questions.map((q) =>
        q._id === id ? { ...q, isFavourite: willBeFavourite } : q,
      );
      this.stats.favourites += willBeFavourite ? 1 : -1;
    });

    try {
      const response = await questionLibraryService.toggleFavourite(id);
      const data = response?.data || response;
      const updatedQuestion = data?.question || data;

      runInAction(() => {
        // Sync with server response
        this.questions = this.questions.map((q) =>
          q._id === id ? updatedQuestion : q,
        );
      });

      return updatedQuestion;
    } catch (err) {
      // Rollback on failure
      runInAction(() => {
        this.questions = prevQuestions;
        this.stats.favourites += willBeFavourite ? -1 : 1;
      });
      showToast.error(err?.message || t("Failed to update favourite."));
      throw err;
    }
  };

  // ─── TOGGLE ARCHIVE ───────────────────────────────────────────────────

  toggleArchive = async (id) => {
    const prevQuestions = [...this.questions];

    try {
      const response = await questionLibraryService.toggleArchive(id);
      const data = response?.data || response;
      const updatedQuestion = data?.question || data;

      runInAction(() => {
        // Remove from current list (archived ↔ active toggle)
        this.questions = this.questions.filter((q) => q._id !== id);
        this.stats.total -= 1;
      });

      showToast.success(
        updatedQuestion.isArchived
          ? t("Question archived successfully.")
          : t("Question restored successfully."),
      );
      await this.fetchQuestions();
      return updatedQuestion;
    } catch (err) {
      runInAction(() => {
        this.questions = prevQuestions;
      });
      showToast.error(err?.message || t("Failed to archive question."));
      throw err;
    }
  };

  // ─── DELETE QUESTION ──────────────────────────────────────────────────

  deleteQuestion = async (id) => {
    const prevQuestions = [...this.questions];

    runInAction(() => {
      this.questions = this.questions.filter((q) => q._id !== id);
      this.stats.total -= 1;
    });

    try {
      await questionLibraryService.deleteQuestion(id);
      showToast.success(t("Question deleted successfully."));
      await this.fetchQuestions();
    } catch (err) {
      runInAction(() => {
        this.questions = prevQuestions;
        this.stats.total += 1;
      });
      showToast.error(err?.message || t("Failed to delete question."));
      throw err;
    }
  };
  duplicateQuestion = async (question) => {
    try {
      const formData = {
        questionText: `${question.questionText} (Copy)`,
        questionType: question.questionType,
        category: question.category,
        options: question.options || [],
      };

      const response = await questionLibraryService.createQuestion(formData);
      const data = response?.data || response;
      const newQuestion = data?.question || data;

      runInAction(() => {
        this.questions = [newQuestion, ...this.questions];
        this.stats.total += 1;
      });

      showToast.success(t("Question duplicated successfully."));
      await this.fetchQuestions();
    } catch (err) {
      showToast.error(err?.message || t("Failed to duplicate question."));
      throw err;
    }
  };
}

export const questionLibraryStore = new QuestionLibraryStore();
